import { useEffect, useMemo, useState } from 'react';

import { useFormContext } from 'react-hook-form';
import { useSelector } from 'react-redux';

import { type IAdditionalFieldConfig } from '../../types/response';
import { ItemsTabFields } from '../../types/fieldEnums';
import { DataType } from '../../types/enums';

import { additionalFieldIdsByNameSelector } from '../../store/selectors';

const defaultValuesByDataType = {
  [DataType.TEXT]: '',
  [DataType.TEXTAREA]: '',
  [DataType.INTEGER]: '',
  [DataType.DECIMAL]: '',
  [DataType.CHECKBOX]: false,
  [DataType.LIST]: null,
  [DataType.DATE]: null,
  [DataType.OPTIONS_FROM_DATABASE]: null,
} as Record<string, string | null | boolean>;

function useDependsOnField({
  config,
  fieldNamePrefix,
  additionalFieldName,
}: {
  config: IAdditionalFieldConfig;
  fieldNamePrefix: string;
  additionalFieldName: string;
}) {
  const additionalFieldIdsByName = useSelector(additionalFieldIdsByNameSelector);
  const { watch, setValue } = useFormContext();

  const { dependsOnFieldName: dependsOnField, dependsOnFieldValues } = config;

  const [showField, setShowField] = useState(!dependsOnField);

  const dependsOnFieldName = useMemo(() => {
    if (!dependsOnField) {
      return null;
    }

    if (dependsOnField in ItemsTabFields) {
      return `${fieldNamePrefix}${dependsOnField}`;
    }

    if (dependsOnField in additionalFieldIdsByName.itemFields) {
      return `${fieldNamePrefix}${additionalFieldIdsByName.itemFields[dependsOnField] as string}`;
    }

    if (dependsOnField in additionalFieldIdsByName.lpcoFields) {
      return additionalFieldIdsByName.lpcoFields[dependsOnField];
    }

    return dependsOnField;
  }, [additionalFieldIdsByName, dependsOnField, fieldNamePrefix]);

  const dependsOnFieldValue = dependsOnFieldName ? (watch(dependsOnFieldName) as SelectOption | string) : null;

  useEffect(() => {
    if (!dependsOnFieldName) {
      return;
    }

    const showFieldNewValue = (dependsOnFieldValue as SelectOption)?.label
      ? dependsOnFieldValues.includes((dependsOnFieldValue as SelectOption)?.label as string) ||
        dependsOnFieldValues.includes((dependsOnFieldValue as SelectOption)?.value)
      : dependsOnFieldValue
      ? dependsOnFieldValues.includes(dependsOnFieldValue as string)
      : false;

    setShowField(showFieldNewValue);
    const currentFieldValue = watch(additionalFieldName);

    if (!showFieldNewValue) {
      setValue(additionalFieldName, defaultValuesByDataType[config.dataType] ?? null);
    } else if (config.defaultValue && !currentFieldValue) {
      setValue(additionalFieldName, config.defaultValue);
    }
  }, [
    additionalFieldName,
    config,
    dependsOnFieldName,
    dependsOnFieldValue,
    dependsOnFieldValues,
    setValue,
    showField,
    watch,
  ]);

  return showField;
}

export default useDependsOnField;
